import { Receipt } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui';
import { FEATURE_KEYS, FEATURES, normalizeFeatures, requiredBy, type Feature } from '@/lib/features';
import { cn } from '@/lib/utils';

/**
 * "מה תרצה לנהל במערכת" — בהרשמה ובהגדרות. חשבוניות בשאלה מפורשת: יש עסקים
 * (מנהלת משרד, עסק שהנה"ח שלו בחוץ) שלא צריכים שום מסך חשבוניות.
 * מודול שמודול אחר צריך (מחירון להצעות מחיר) — נעול, עם הסבר.
 */
export function FeaturePicker({ value, onChange }: { value: Feature[]; onChange: (next: Feature[]) => void }) {
  const { t } = useTranslation();
  const selected = normalizeFeatures(value);
  const toggle = (f: Feature, on: boolean) => {
    const next = on ? [...selected, f] : selected.filter((x) => x !== f);
    onChange(normalizeFeatures(next));
  };
  const invoicing = selected.includes('invoicing');

  return (
    <div className="space-y-3">
      <fieldset className="rounded-(--radius-lg) border border-border p-3">
        <legend className="flex items-center gap-1.5 px-1 text-sm font-medium text-fg">
          <Receipt className="size-4 text-accent" aria-hidden />
          {t('featurePicker.invoicingQuestion')}
        </legend>
        <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('featurePicker.invoicingLabel')}>
          {[
            { on: true, title: t('actions.yes'), hint: t('featurePicker.invoicingYesHint') },
            { on: false, title: t('featurePicker.invoicingNo'), hint: t('featurePicker.invoicingNoHint') },
          ].map((o) => (
            <button
              key={String(o.on)}
              type="button"
              role="radio"
              aria-checked={invoicing === o.on}
              onClick={() => toggle('invoicing', o.on)}
              className={cn(
                'rounded-(--radius-md) border px-3 py-2 text-start transition-colors',
                invoicing === o.on ? 'border-accent bg-accent-subtle' : 'border-border bg-surface hover:border-border-strong',
              )}
            >
              <span className="block text-sm font-medium text-fg">{o.title}</span>
              <span className="block text-2xs text-fg-muted">{o.hint}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <ul className="divide-y divide-border rounded-(--radius-lg) border border-border">
        {FEATURE_KEYS.filter((f) => f !== 'invoicing').map((f) => {
          const on = selected.includes(f);
          const lockedBy = on ? requiredBy(f, selected) : null;
          return (
            <li key={f}>
              <label className="flex items-start justify-between gap-3 px-3 py-2.5">
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-fg">{FEATURES[f].label}</span>
                  <span className="block text-2xs text-fg-muted">
                    {lockedBy ? t('featurePicker.requiredBy', { feature: FEATURES[lockedBy].label }) : FEATURES[f].hint}
                  </span>
                </span>
                <Switch
                  checked={on}
                  disabled={!!lockedBy}
                  onCheckedChange={(v) => toggle(f, v)}
                  aria-label={FEATURES[f].label}
                />
              </label>
            </li>
          );
        })}
      </ul>
      <p className="text-2xs text-fg-subtle">{t('featurePicker.alwaysOn')}</p>
    </div>
  );
}
